import * as React from 'react'
import { useSelector } from 'react-redux'
import { selectAppDataUrls } from '@store/selectors/app-data-selector'
import { ProductKinds } from '@components/payments/payment-selection-view'
import { useApiRequest } from '@hooks/use-form-request'
import { commonObjectGet } from '@requests/basic-requests'
import { normalizePaymentSuccessKind } from '@hooks/gtm/payment-success-kind-aliases'

export const usePaymentSuccessProductDetails = <T = unknown>(
  kind: string | undefined,
  token: string | undefined,
  action: (productDetails: T) => void,
): void => {
  const urls = useSelector(selectAppDataUrls)
  const normalizedKind = normalizePaymentSuccessKind(kind)

  const productsDetailsUrls = {
    [ProductKinds.SUBSCRIPTIONS_WITH_BENEFITS]: urls.subscription.details,
    [ProductKinds.GO_HOLIDAY]: urls.subscription.details,
    [ProductKinds.CODES_CONVERSION]: urls.codes.details,
    [ProductKinds.RESERVATION_DETAILS]: urls.reservation.details,
    [ProductKinds.SUBSCRIPTION_CONTRACT]: urls.subscription_contract.details,
    [ProductKinds.ENDLESS_HOLIDAY]: urls.product_packages.details,
  }

  const { action: sendPaymentSuccessOrderEvent } = useApiRequest(async () => {
    if (!normalizedKind || !token) {
      // eslint-disable-next-line no-console
      console.log('[Tradedoubler] skipping payment success product details fetch', {
        reason: 'missing kind or token',
        kind,
        normalizedKind,
        token,
      })
      return
    }

    const productDetailsUrl = productsDetailsUrls[normalizedKind]
    if (!productDetailsUrl) {
      // eslint-disable-next-line no-console
      console.log('[Tradedoubler] missing product details URL', {
        kind,
        normalizedKind,
        token,
      })
      return
    }

    // eslint-disable-next-line no-console
    console.log('[Tradedoubler] fetching payment success product details', {
      kind,
      normalizedKind,
      token,
      productDetailsUrl,
    })

    try {
      const productDetails = await commonObjectGet<T>(productDetailsUrl.replace('replace-me', String(token)))

      // eslint-disable-next-line no-console
      console.log('[Tradedoubler] payment success product details loaded', {
        kind,
        normalizedKind,
        token,
      })

      action(productDetails)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('[Tradedoubler] payment success product details fetch failed', {
        kind,
        normalizedKind,
        token,
      })
      throw error
    }
  })

  React.useEffect(() => {
    if (normalizedKind && token) sendPaymentSuccessOrderEvent()
  }, [])
}
